import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError, STAGES } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import useLivePoll from "@/hooks/useLivePoll";
import LiveDot from "@/components/LiveDot";
import { Scissors, Layers, Slice, Stethoscope } from "lucide-react";

const BUCKETS = [
  { key: "to_gross", label: "To gross — no blocks yet", icon: Slice, next: "Grossed" },
  { key: "to_cut", label: "To embed & cut — blocks with no slides", icon: Layers, next: "Cut" },
  { key: "recuts_pending", label: "Recuts & specials pending", icon: Scissors, next: null },
  { key: "ready_for_pathologist", label: "Ready for the pathologist", icon: Stethoscope, next: null },
];

export default function TechWorklist() {
  const { can } = useAuth();
  const [data, setData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(async (silent = false) => {
    try {
      const [w, s] = await Promise.all([api.get("/tech-worklist"), api.get("/tech-worklist/summary")]);
      setData(w.data);
      setSummary(s.data);
    } catch (e) { if (!silent) toast.error(apiError(e)); }
  }, []);

  useEffect(() => { load(); }, [load]);
  const lastSync = useLivePoll(() => load(true), 6000);

  const advance = async (row, status) => {
    setBusy(row.id);
    try {
      await api.post(`/cases/${row.id}/status`, { status, note: `Advanced to ${status} from the bench worklist` });
      toast.success(`${row.accession_number} → ${status}`);
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  if (!data) return <div className="text-sm text-slate-500" data-testid="tech-worklist-loading">Loading bench worklist…</div>;
  const target = summary?.tat_target_hours || 48;

  return (
    <div className="space-y-6" data-testid="tech-worklist-page">
      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Bench Worklist</h1>
        <p className="mt-1 flex flex-wrap items-center gap-2 text-sm text-slate-600">
          {summary ? `${summary.outstanding} cases need histology · ${summary.stat} STAT · ${summary.overdue} over ${target}h` : "Loading…"}
          <LiveDot lastSync={lastSync} testid="tech-worklist-live-indicator" />
        </p>
      </div>

      {BUCKETS.map(({ key, label, icon: Icon, next }) => {
        const list = data[key] || [];
        return (
          <section key={key} className="border border-slate-200" data-testid={`tech-section-${key}`}>
            <header className="flex items-center gap-2 border-b border-slate-200 bg-slate-50 px-3 py-2">
              <Icon size={15} className="text-[#134e4a]" />
              <h2 className="text-base font-semibold text-slate-800 md:text-lg">{label}</h2>
              <span className="num ml-auto text-sm font-semibold text-slate-600">{list.length}</span>
            </header>
            {list.length === 0 ? (
              <p className="px-3 py-6 text-center text-sm text-slate-500">Nothing here.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[920px] text-sm">
                  <thead className="bg-white text-left text-xs uppercase tracking-wide text-slate-500">
                    <tr className="border-b border-slate-200">
                      <th className="px-3 py-2">Priority</th><th className="px-3 py-2">Case</th><th className="px-3 py-2">Patient</th>
                      <th className="px-3 py-2">Specimen</th><th className="px-3 py-2 text-right">Age</th>
                      <th className="px-3 py-2 text-right">Blocks / Slides</th><th className="px-3 py-2">Stage</th>
                      <th className="px-3 py-2 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.slice(0, 100).map((r) => {
                      const overdue = r.age_hours > target;
                      return (
                        <tr key={r.id} className="border-b border-slate-100 row-hover hover:bg-slate-50" data-testid={`tech-row-${r.id}`}>
                          <td className="px-3 py-2">
                            {r.priority === "STAT"
                              ? <span className="bg-red-800 px-1.5 py-0.5 text-[11px] font-bold text-white">STAT</span>
                              : <span className="text-slate-500">Routine</span>}
                          </td>
                          <td className="px-3 py-2">
                            <Link to={`/cases/${r.id}`} className="font-mono font-semibold text-[#134e4a] hover:underline">{r.accession_number}</Link>
                          </td>
                          <td className="px-3 py-2 text-slate-800">{r.patient_name}</td>
                          <td className="px-3 py-2 text-slate-700">{r.specimen_type || "—"}</td>
                          <td className={`num px-3 py-2 text-right font-semibold ${overdue ? "text-red-800" : "text-slate-700"}`}>
                            {r.age_days >= 1 ? `${r.age_days}d` : `${r.age_hours}h`}
                          </td>
                          <td className="num px-3 py-2 text-right text-slate-700">{r.block_count} / {r.slide_count}</td>
                          <td className="px-3 py-2 text-slate-700">
                            {r.status}
                            {r.pending_recuts > 0 && (
                              <span className="ml-2 bg-amber-100 px-1.5 py-0.5 text-[11px] font-semibold text-amber-900">
                                {r.pending_recuts} recut
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-right">
                            {key === "recuts_pending" ? (
                              <Link to="/recuts" className="text-sm font-semibold text-[#134e4a] hover:underline">Recut queue →</Link>
                            ) : next && can("status:write") && r.status !== next && STAGES.includes(next) ? (
                              <Button size="sm" disabled={busy === r.id} onClick={() => advance(r, next)}
                                data-testid={`tech-advance-${r.id}`} className="h-9 bg-[#134e4a] hover:bg-[#0f3f3c]">
                                Mark {next}
                              </Button>
                            ) : (
                              <Link to={`/cases/${r.id}`} className="text-sm font-semibold text-[#134e4a] hover:underline">Open →</Link>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {list.length > 100 && (
                  <p className="px-3 py-2 text-xs text-slate-500">Showing the first 100 of {list.length}.</p>
                )}
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}
